import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useWorkspace, useWorkspaceCan } from "./WorkspaceContext";

type Accomplishment = {
  id: string;
  title: string;
  description: string | null;
  occurredAt: string;
};

const fmtDate = (d: string) => new Date(d).toLocaleDateString();

export function AccomplishmentsTab() {
  const { campaignId } = useWorkspace();
  // #2504-2506: manage controls only for super_admin / the owning campaign_owner.
  const { manageItems } = useWorkspaceCan();
  const qc = useQueryClient();
  const base = `/api/admin/campaigns/${campaignId}/accomplishments`;
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Accomplishment | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Accomplishment | null>(null);

  const { data } = useQuery({
    queryKey: ["accomplishments", campaignId],
    queryFn: () => api.get<{ items: Accomplishment[] }>(base),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${base}/${id}`),
    onSuccess: () => {
      toast.success("Accomplishment deleted");
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ["accomplishments", campaignId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-head text-lg font-semibold">Accomplishments</h2>
          <p className="text-xs text-muted-foreground">Wins and milestones worth remembering for this campaign.</p>
        </div>
        {manageItems && <Button onClick={() => setCreateOpen(true)}>New accomplishment</Button>}
      </div>

      <Card>
        <ul className="divide-y divide-admin-rule">
          {items.map((a) => (
            <li key={a.id} className="p-3 flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-sm">{a.title}</div>
                {a.description && <div className="text-xs text-muted-foreground">{a.description}</div>}
                <div className="text-[11px] text-muted-foreground mt-0.5">{fmtDate(a.occurredAt)}</div>
              </div>
              {manageItems && (
                <div className="flex shrink-0 gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing(a)}
                    aria-label="Edit accomplishment"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => setPendingDelete(a)}
                    aria-label="Delete accomplishment"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </li>
          ))}
          {items.length === 0 && (
            <li className="p-6 text-center text-xs text-muted-foreground">No accomplishments logged yet.</li>
          )}
        </ul>
      </Card>

      <CreateAccomplishmentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        base={base}
        onSaved={() => qc.invalidateQueries({ queryKey: ["accomplishments", campaignId] })}
      />

      <EditAccomplishmentDialog
        item={editing}
        base={base}
        onClose={() => setEditing(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ["accomplishments", campaignId] })}
      />

      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{pendingDelete?.title}"?</DialogTitle>
            <DialogDescription>This permanently removes the accomplishment.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Fix a typo without delete+recreate (audit follow-up: PATCH existed with no UI).
function EditAccomplishmentDialog({
  item,
  base,
  onClose,
  onSaved,
}: {
  item: Accomplishment | null;
  base: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({ title: "", description: "", occurredAt: "" });
  useEffect(() => {
    if (item) {
      setForm({
        title: item.title,
        description: item.description ?? "",
        occurredAt: item.occurredAt ? item.occurredAt.slice(0, 10) : "",
      });
    }
  }, [item]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`${base}/${item!.id}`, {
        title: form.title,
        description: form.description || null,
        occurredAt: form.occurredAt || undefined,
      }),
    onSuccess: () => {
      toast.success("Saved");
      onSaved();
      onClose();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  if (!item) return null;
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit accomplishment</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.occurredAt} onChange={(e) => setForm((f) => ({ ...f, occurredAt: e.target.value }))} />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!form.title || save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateAccomplishmentDialog({
  open,
  onOpenChange,
  base,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  base: string;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({ title: "", description: "", occurredAt: "" });
  const reset = () => setForm({ title: "", description: "", occurredAt: "" });

  const save = useMutation({
    mutationFn: () =>
      api.post(base, {
        title: form.title,
        description: form.description || undefined,
        occurredAt: form.occurredAt || undefined,
      }),
    onSuccess: () => {
      toast.success("Accomplishment logged");
      onSaved();
      reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New accomplishment</DialogTitle>
          <DialogDescription>This entry belongs only to this campaign.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.occurredAt} onChange={(e) => setForm((f) => ({ ...f, occurredAt: e.target.value }))} />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!form.title || save.isPending}>
            {save.isPending ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
